"use client"

import { useCallback } from "react"

import useAnalytics from "@/hooks/useAnalytics"
import { buildLabel } from "@/lib/analytics/labels"
import {
  flushPendingQotdAttempts,
  getPendingQotdAttempts
} from "@/lib/qotd/pending"

/**
 * Hook that returns a function to locate pending QOTD attempts, emit telemetry,
 * flush them to the API, and best-effort reload the page.
 */
export function useQotdPendingFlush() {
  const { trackEvent } = useAnalytics()

  const flush = useCallback(async () => {
    try {
      const pending = getPendingQotdAttempts()
      if (pending.length === 0) return

      const labels = new Set<string>()
      for (const p of pending) {
        const subject = p.attempt.subjectSlug
        labels.add(buildLabel("subjects", subject, "question-of-the-day"))
      }

      void trackEvent({
        action: "QOTD Pending Attempts Found",
        category: "quiz",
        label: Array.from(labels).join("|")
      })

      const results = await flushPendingQotdAttempts(pending.map((p) => p.key))
      const ok = results.filter((r) => r.success).length
      const total = results.length

      void trackEvent({
        action: "QOTD Pending Attempts Flushed",
        category: "quiz",
        label: `${ok}/${total}`
      })

      // Only reload if we successfully flushed at least one attempt
      // This prevents infinite reload loops when API calls fail
      if (ok > 0) {
        try {
          if (typeof window !== "undefined") {
            window.location.reload()
          }
        } catch {
          // ignore
        }
      }
    } catch {
      // ignore top-level failures
    }
  }, [trackEvent])

  return flush
}
